import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import {
  BookOpen, DoorOpen, Calendar, ClipboardList,
  GraduationCap, LogOut, ArrowUpRight, Clock
} from 'lucide-react';
import api from '../utils/api';

/* ─── colour tokens (match sidebar palette) ─────────────────────────── */
const accent = {
  purple: { bg: '#EEEDFE', text: '#534AB7', dot: '#7F77DD' },
  blue:   { bg: '#E6F1FB', text: '#185FA5', dot: '#378ADD' },
  teal:   { bg: '#E1F5EE', text: '#0F6E56', dot: '#1D9E75' },
  amber:  { bg: '#FAEEDA', text: '#854F0B', dot: '#BA7517' },
  red:    { bg: '#FCEBEB', text: '#A32D2D', dot: '#E24B4A' },
};

/* ─── StatCard ───────────────────────────────────────────────────────── */
function StatCard({ icon: Icon, label, value, color, loading, to }) {
  const navigate = useNavigate();
  const { bg, text } = accent[color];
  return (
    <button
      onClick={() => navigate(to)}
      className="group bg-white border border-[#E2E0D8] rounded-2xl p-5 flex flex-col gap-3
                 hover:border-[#C8C6BE] hover:shadow-sm transition-all duration-150 text-left w-full"
    >
      <div className="flex items-center justify-between">
        <div
          className="w-8 h-8 rounded-xl flex items-center justify-center"
          style={{ background: bg }}
        >
          <Icon className="w-4 h-4" style={{ color: text }} />
        </div>
        <ArrowUpRight
          className="w-3.5 h-3.5 text-[#C8C6BE] group-hover:text-[#888780]
                     group-hover:translate-x-0.5 group-hover:-translate-y-0.5
                     transition-all duration-150"
        />
      </div>
      <div>
        <p className="text-3xl font-light text-[#1a1a18] leading-none tabular-nums">
          {loading ? <span className="text-[#D3D1C7]">—</span> : value}
        </p>
        <p className="text-xs text-[#888780] mt-1.5 tracking-wide">{label}</p>
      </div>
    </button>
  );
}

/* ─── Section heading ────────────────────────────────────────────────── */
function SectionHeading({ children, action, onAction }) {
  return (
    <div className="flex items-center justify-between mb-4">
      <h2 className="text-[13px] font-medium text-[#1a1a18] uppercase tracking-widest">
        {children}
      </h2>
      {action && (
        <button
          onClick={onAction}
          className="text-[11px] text-[#888780] hover:text-[#1a1a18] transition-colors"
        >
          {action}
        </button>
      )}
    </div>
  );
}

/* ─── Exam row ───────────────────────────────────────────────────────── */
function ExamRow({ exam, last }) {
  const date = new Date(exam.exam_date);
  const day   = date.toLocaleDateString('en-GB', { weekday: 'short' });
  const dayN  = date.getDate();
  const month = date.toLocaleDateString('en-GB', { month: 'short' });

  return (
    <div className={`flex items-center gap-4 py-3 ${!last ? 'border-b border-[#F1EFE8]' : ''}`}>
      {/* date chip */}
      <div className="flex-shrink-0 w-11 text-center">
        <p className="text-[10px] text-[#888780] uppercase tracking-wide leading-none">{day}</p>
        <p className="text-[20px] font-light text-[#1a1a18] leading-tight tabular-nums">{dayN}</p>
        <p className="text-[10px] text-[#888780] leading-none">{month}</p>
      </div>
      <div className="w-px h-8 bg-[#E2E0D8] flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="text-[13px] font-medium text-[#2C2C2A] truncate">
          {exam.course_code} — {exam.course_name}
        </p>
        <p className="text-[11px] text-[#888780] mt-0.5 flex items-center gap-1.5">
          <Clock className="w-3 h-3 inline flex-shrink-0" />
          {exam.time_slot_label} &middot; {exam.room_name}
        </p>
      </div>
      <div
        className="flex-shrink-0 w-1.5 h-1.5 rounded-full"
        style={{ background: accent.amber.dot }}
      />
    </div>
  );
}

/* ─── Year row ───────────────────────────────────────────────────────── */
function YearRow({ year, last }) {
  const fmt = d => new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  return (
    <div className={`flex items-center justify-between py-3 ${!last ? 'border-b border-[#F1EFE8]' : ''}`}>
      <div>
        <p className="text-[13px] font-medium text-[#2C2C2A]">{year.name}</p>
        <p className="text-[11px] text-[#888780] mt-0.5">{fmt(year.start_date)} – {fmt(year.end_date)}</p>
      </div>
      {year.is_active ? (
        <span className="text-[11px] font-medium px-2.5 py-1 bg-[#EAF3DE] text-[#3B6D11]
                         border border-[#C0DD97] rounded-full flex-shrink-0">
          Active
        </span>
      ) : (
        <span className="text-[11px] px-2.5 py-1 bg-[#F1EFE8] text-[#888780]
                         border border-[#E2E0D8] rounded-full flex-shrink-0">
          Closed
        </span>
      )}
    </div>
  );
}

/* ─── Dashboard ──────────────────────────────────────────────────────── */
export default function DashboardPage() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const { data: courses = [], isLoading: cLoading }  = useQuery(['courses'],          () => api.get('/courses').then(r => r.data));
  const { data: rooms   = [], isLoading: rLoading }  = useQuery(['rooms'],             () => api.get('/rooms').then(r => r.data));
  const { data: years   = [], isLoading: yLoading }  = useQuery(['years'],             () => api.get('/academic-years').then(r => r.data));
  const { data: classes = [], isLoading: ctLoading } = useQuery(['class-timetables'], () => api.get('/class-timetables').then(r => r.data));
  const { data: exams   = [], isLoading: etLoading } = useQuery(['exam-timetables'],  () => api.get('/exam-timetables').then(r => r.data));

  const activeYear = years.find(y => y.is_active);

  const upcomingExams = exams
    .filter(e => new Date(e.exam_date) >= new Date())
    .sort((a, b) => new Date(a.exam_date) - new Date(b.exam_date))
    .slice(0, 5);

  const handleLogout = () => { logout(); navigate('/login'); };

  const stats = [
    { icon: GraduationCap, label: 'Academic years', value: years.length,   color: 'purple', loading: yLoading,  to: '/academic-years'  },
    { icon: BookOpen,      label: 'Total courses',   value: courses.length, color: 'blue',   loading: cLoading,  to: '/courses'         },
    { icon: DoorOpen,      label: 'Rooms & venues',  value: rooms.length,   color: 'teal',   loading: rLoading,  to: '/rooms'           },
    { icon: Calendar,      label: 'Class slots',     value: classes.length, color: 'amber',  loading: ctLoading, to: '/class-timetable' },
    { icon: ClipboardList, label: 'Exam slots',      value: exams.length,   color: 'red',    loading: etLoading, to: '/exam-timetable'  },
  ];

  return (
    <div className="min-h-screen bg-[#F7F6F2]">

      {/* ── Top bar ─────────────────────────────────────────────────────── */}
      <header className="bg-[#F7F6F2] border-b border-[#E2E0D8] px-8 py-4
                         flex items-center justify-between sticky top-0 z-10">
        <div>
          <p className="text-[10px] text-[#B4B2A9] uppercase tracking-[0.12em] mb-0.5">
            Timetabling System
          </p>
          <h1 className="text-[18px] font-medium text-[#1a1a18] leading-none">Dashboard</h1>
        </div>

        <div className="flex items-center gap-3">
          {activeYear && (
            <span className="hidden sm:inline-flex text-[11px] font-medium px-3 py-1
                             bg-[#EAF3DE] text-[#3B6D11] border border-[#C0DD97] rounded-full">
              {activeYear.name} · Active
            </span>
          )}

          {/* User chip */}
          <div className="flex items-center gap-2.5 bg-white border border-[#E2E0D8]
                          rounded-xl px-3 py-2">
            <div className="w-7 h-7 rounded-full bg-[#1a1a18] flex items-center
                            justify-center flex-shrink-0">
              <span className="text-[#F7F6F2] text-[10px] font-medium">
                {user?.fullName?.charAt(0).toUpperCase()}
              </span>
            </div>
            <div className="hidden sm:block">
              <p className="text-[12px] font-medium text-[#1a1a18] leading-tight">
                {user?.fullName}
              </p>
              <p className="text-[10px] text-[#888780]">{user?.email}</p>
            </div>
            <button
              onClick={handleLogout}
              title="Sign out"
              className="ml-1 p-1 text-[#B4B2A9] hover:text-[#A32D2D] transition-colors"
            >
              <LogOut className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </header>

      {/* ── Body ────────────────────────────────────────────────────────── */}
      <main className="px-8 py-8 max-w-7xl mx-auto">

        {/* Stat cards */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-8">
          {stats.map(s => <StatCard key={s.label} {...s} />)}
        </div>

        {/* Two-column lower section */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">

          {/* Upcoming exams — wider */}
          <div className="lg:col-span-3 bg-white border border-[#E2E0D8] rounded-2xl p-6">
            <SectionHeading
              action="View all →"
              onAction={() => navigate('/exam-timetable')}
            >
              Upcoming exams
            </SectionHeading>

            {upcomingExams.length === 0 ? (
              <div className="py-10 text-center">
                <p className="text-[13px] text-[#B4B2A9]">No upcoming exams scheduled.</p>
              </div>
            ) : (
              upcomingExams.map((exam, i) => (
                <ExamRow key={exam.id} exam={exam} last={i === upcomingExams.length - 1} />
              ))
            )}
          </div>

          {/* Academic years — narrower */}
          <div className="lg:col-span-2 bg-white border border-[#E2E0D8] rounded-2xl p-6">
            <SectionHeading
              action="Manage →"
              onAction={() => navigate('/academic-years')}
            >
              Academic years
            </SectionHeading>

            {years.length === 0 ? (
              <div className="py-10 text-center">
                <p className="text-[13px] text-[#B4B2A9]">No academic years created yet.</p>
              </div>
            ) : (
              years.map((y, i) => (
                <YearRow key={y.id} year={y} last={i === years.length - 1} />
              ))
            )}
          </div>

        </div>
      </main>
    </div>
  );
}